import { FC, useMemo, useState } from 'react';

import { startCase } from 'lodash';
import { Button } from 'ui/components/common';
import useModal from 'ui/partials/Modal/useModal';
import { cn } from 'utils/helpers';

import DeleteValidation from './DeleteValidationInput';

type PROPS = {
  text?: string;
  onClickDelete: () => void;
  onCancel?: () => void;
  isMany?: boolean;
  isLoading?: boolean;
  isDeletable?: boolean;
  validation?: boolean;
};

const DeleteItemModal: FC<PROPS> = ({
  text = 'note',
  onClickDelete,
  onCancel,
  isMany = false,
  isLoading = false,
  isDeletable = true,
  validation = true,
}) => {
  const { hideModal } = useModal();
  const [isValidated, setIsValidated] = useState<boolean>(false);

  const handleCancel = () => {
    if (onCancel) {
      return onCancel();
    } else {
      return hideModal();
    }
  };

  const validate = useMemo((): boolean => {
    if (!validation) return isLoading;

    return !isLoading && validation && !isValidated;
  }, [isLoading, isValidated, validation]);

  return (
    <>
      <div className="space-y-[6px]">
        <p className={cn('font-semibold text-sm', 'tracking-wider leading-6')}>
          {isDeletable
            ? `Delete ${startCase(text)}`
            : `Unable to Delete ${text}`}
        </p>
        <p className="text-xs leading-5">
          {isDeletable
            ? `Are you sure you want to delete ${isMany ? 'these' : 'this'} ${text}?`
            : // eslint-disable-next-line max-len
              `Unable to delete ${text} since it is still associated with another record. Remove association first to proceed with the deletion.`}
        </p>
        {isDeletable && validation && (
          <DeleteValidation onChange={setIsValidated} />
        )}
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'pt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        {isDeletable ? (
          <>
            <Button
              onClick={onClickDelete}
              label="PROCEED"
              size="sm"
              intent="destructive"
              disabled={validate}
            />
            <Button
              onClick={handleCancel}
              label="CANCEL"
              intent={'tertiary'}
              size="sm"
              disabled={isLoading}
            />
          </>
        ) : (
          <Button onClick={handleCancel} label="OK" disabled={isLoading} />
        )}
      </div>
    </>
  );
};

export default DeleteItemModal;
